import { isArray, isEmpty } from "lodash";
import moment from "moment-mini";
import { fork, put, select, takeLatest } from "redux-saga/effects";

import ApiClient from "../../../api/ApiClient";
import { actions as alertsActions } from "../alerts";
import { getUserID } from "../auth/login/selector";
import { getLocale, getTranslations } from "../translations/selector";
import {
  addChartAnalysisRoutine,
  deleteChartAnalysisRoutine,
  fetchChartAnalysesDetailsRoutine,
  fetchChartAnalysesRoutine,
  setChartAnalysisVisibility,
  updateChartAnalysisRoutine
} from "./index";
import { getChartAnalysesList } from "./selector";

function* showSuccessfullyAddedAlert() {
  yield put(
    alertsActions.addSuccessAlert({
      title: "Successfully added chart analysis"
    })
  );
}

function* showErrorWhileAddingAlert() {
  yield put(
    alertsActions.addErrorAlert({
      title: "There was an error when saving your chart analysis"
    })
  );
}

function* showErrorWhileDeletingAlert() {
  yield put(
    alertsActions.addErrorAlert({
      title: "There was an error when removing your chart analysis"
    })
  );
}

function* showSuccessfullyRemovedAlert() {
  yield put(
    alertsActions.addSuccessAlert({
      title: "Successfully removed chart analysis"
    })
  );
}

function* showSuccessfullyUpdatedAlert() {
  yield put(
    alertsActions.addSuccessAlert({
      title: "Successfully updated chart analysis"
    })
  );
}

function* showErrorWhileUpdatingAlert() {
  yield put(
    alertsActions.addErrorAlert({
      title: "There was an error when updating your chart analysis"
    })
  );
}

function* showSuccessfullyPublishedAlert(isPublic) {
  yield put(
    alertsActions.addSuccessAlert({
      title: isPublic
        ? "analysis_published_successfully"
        : "analysis_unpublished_successfully"
    })
  );
}

function* showErrorWhilePublishingAlert(isPublic) {
  yield put(
    alertsActions.addErrorAlert({
      title: isPublic ? "analysis_publish_error" : "analysis_unpublish_error"
    })
  );
}

function* showNotPermittedToPublishAlert() {
  yield put(
    alertsActions.addErrorAlert({
      title: "analysis_publish_not_permitted"
    })
  );
}

function* showSuccessfullyAppliedAlert() {
  yield put(
    alertsActions.addSuccessAlert({
      title: "Successfully applied chart analysis"
    })
  );
}

function* showErrorWhileApplyingAlert() {
  yield put(
    alertsActions.addErrorAlert({
      title: "There was an error while applying your chart analysis"
    })
  );
}

function* onFetchChartAnalysis({ payload } = {}) {
  const stockId = payload?.stockId;

  if (!stockId) {
    yield put(fetchChartAnalysesRoutine.failure());
    return;
  }

  try {
    const { data } = yield ApiClient.get({
      urlPath: "app.chartAnalyses.list",
      skipAccessCheck: true,
      variables: {
        stockId
      }
    });

    yield put(fetchChartAnalysesRoutine.success(data));
  } catch (error) {
    yield put(fetchChartAnalysesRoutine.failure());
  }
}

function isAnalysisNameExists(name, analyses = []) {
  return (
    typeof analyses.find(({ name: aName }) => aName === name) !== "undefined"
  );
}

/**
 * Reads what both auto-generated analysis names are built from: the current list, and the
 * translated label under `translationKey`, falling back to the raw key when it is not imported yet.
 */
function* getAnalysisNamingContext(translationKey) {
  const analyses = (yield select(getChartAnalysesList)) ?? [];
  const translations = yield select(getTranslations);
  const locale = yield select(getLocale);

  return {
    analyses,
    translatedName: translations?.[locale]?.[translationKey] ?? translationKey
  };
}

/**
 * Counts up from `startNumber` until `buildName` produces a name none of `analyses` uses — the
 * backend enforces name uniqueness per (user, stock) and rejects a collision with a 400.
 */
function findFreeAnalysisName(buildName, analyses, startNumber) {
  let number = startNumber;
  let name = buildName(number);

  while (isAnalysisNameExists(name, analyses)) {
    number++;
    name = buildName(number);
  }

  return name;
}

export function* getDefaultAnalysisName() {
  const { analyses, translatedName } = yield* getAnalysisNamingContext(
    "default_analysis_name"
  );

  return findFreeAnalysisName(
    number => `${translatedName} ${number}`,
    analyses,
    analyses.length + 1
  );
}

/**
 * Name for a one-click published analysis: "analiza techniczna DD.MM.YYYY" (SQUABER-540).
 *
 * The backend has no server-side default naming, so publishing twice on the same day would
 * collide — hence the numeric suffix fallback. Only the caller's own analyses can collide, since
 * the list also carries other users' public ones.
 */
export function* getPublishedAnalysisName() {
  const { analyses, translatedName } = yield* getAnalysisNamingContext(
    "published_analysis_name"
  );
  const userID = yield select(getUserID);

  const baseName = `${translatedName} ${moment().format("DD.MM.YYYY")}`;

  return findFreeAnalysisName(
    number => (number === 1 ? baseName : `${baseName} (${number})`),
    analyses.filter(({ user }) => user === userID),
    1
  );
}

export function* onAddChartAnalysis({ payload }) {
  const {
    description,
    settings: chartSettings,
    is_public: isPublic = false,
    stockId
  } = payload ?? {};

  const name =
    payload?.name ??
    (isPublic
      ? yield getPublishedAnalysisName()
      : yield getDefaultAnalysisName());

  try {
    const settings = yield JSON.stringify(chartSettings);

    yield ApiClient.post({
      urlPath: "app.chartAnalyses.list",
      data: {
        name,
        description,
        settings,
        is_public: isPublic
      },
      variables: { stockId }
    });

    if (isPublic) {
      yield showSuccessfullyPublishedAlert(true);
    } else {
      yield showSuccessfullyAddedAlert();
    }

    yield put(fetchChartAnalysesRoutine({ stockId }));
    yield put(addChartAnalysisRoutine.success());
  } catch (error) {
    if (error?.response?.status === 403) {
      yield showNotPermittedToPublishAlert();
      yield put(addChartAnalysisRoutine.failure());

      return;
    }

    yield showErrorWhileAddingAlert();

    const errors = error?.response?.data?.non_field_errors;

    if (isArray(errors) && !isEmpty(errors)) {
      yield put(addChartAnalysisRoutine.failure(errors[0]));
    } else {
      yield put(addChartAnalysisRoutine.failure());
    }
  }
}

export function* onUpdateChartAnalysis({ payload }) {
  const { id, name, description, stockId } = payload;

  try {
    yield ApiClient.patch({
      urlPath: "app.chartAnalyses.update",
      skipAccessCheck: true,
      variables: {
        stockId,
        analysisId: id
      },
      data: {
        name,
        description
      }
    });

    yield showSuccessfullyUpdatedAlert();
    yield put(fetchChartAnalysesRoutine({ stockId }));
    yield put(updateChartAnalysisRoutine.success());
  } catch (error) {
    yield showErrorWhileUpdatingAlert();

    const errors = error?.response?.data?.non_field_errors;

    yield put(updateChartAnalysisRoutine.failure(errors?.[0] || error));
  }
}

export function* onSetChartAnalysisVisibility({ payload }) {
  const { id: analysisId, isPublic, stockId } = payload;

  try {
    yield ApiClient.patch({
      urlPath: "app.chartAnalyses.update",
      skipAccessCheck: true,
      variables: {
        stockId,
        analysisId
      },
      data: {
        is_public: isPublic
      }
    });

    yield showSuccessfullyPublishedAlert(isPublic);
    yield put(fetchChartAnalysesRoutine({ stockId }));
    yield put(setChartAnalysisVisibility.success());
  } catch (error) {
    // The backend guards the is_public transition with a permission check and answers 403.
    // The detail message is translated, so the status code is the only reliable discriminator.
    if (error?.response?.status === 403) {
      yield showNotPermittedToPublishAlert();
      yield put(setChartAnalysisVisibility.failure(error));

      return;
    }

    yield showErrorWhilePublishingAlert(isPublic);
    yield put(setChartAnalysisVisibility.failure(error));
  }
}

function* onDeleteChartAnalysis({ payload }) {
  const { id: analysisId, stockId } = payload;

  try {
    yield ApiClient.delete({
      urlPath: "app.chartAnalyses.remove",
      skipAccessCheck: true,
      variables: {
        stockId,
        analysisId
      }
    });

    yield put(fetchChartAnalysesRoutine({ stockId }));
    yield showSuccessfullyRemovedAlert();
    yield put(deleteChartAnalysisRoutine.success());
  } catch (error) {
    yield showErrorWhileDeletingAlert();
    yield put(deleteChartAnalysisRoutine.failure(error));
  }
}

function* onFetchChartAnalysisDetails({ payload: url }) {
  try {
    const { data } = yield ApiClient.get({
      url,
      skipAuthorizationHeader: true
    });

    yield put(fetchChartAnalysesDetailsRoutine.success(data));
    yield showSuccessfullyAppliedAlert();
  } catch (e) {
    yield put(fetchChartAnalysesDetailsRoutine.failure(e));
    yield showErrorWhileApplyingAlert();
  }
}

function* watchChartAnalysis() {
  yield takeLatest(fetchChartAnalysesRoutine, onFetchChartAnalysis);
  yield takeLatest(
    fetchChartAnalysesDetailsRoutine,
    onFetchChartAnalysisDetails
  );
  yield takeLatest(updateChartAnalysisRoutine, onUpdateChartAnalysis);
  yield takeLatest(setChartAnalysisVisibility, onSetChartAnalysisVisibility);
  yield takeLatest(deleteChartAnalysisRoutine, onDeleteChartAnalysis);
  yield takeLatest(addChartAnalysisRoutine, onAddChartAnalysis);
}

export default [fork(watchChartAnalysis)];
